/**
 * Forecast tab — fact vs forecast + 2σ band, horizon 7/14/30, MAPE 14d.
 * Horizon persisted in localStorage. Chart: Y-axis ₽, tooltips, color-blind safe palette.
 */
import { useEffect, useState, useCallback } from 'react';
import { getAdminForecast, type AdminForecastData, type AdminForecastPoint } from '../../api/admin.api';

const HORIZONS = [1, 7, 14, 30] as const;
const STORAGE_KEY_HORIZON = 'admin_forecast_horizon';

// Color-blind safe: fact = blue, forecast = orange, 2σ band = orange alpha
const COLOR_FACT = '#2563eb';
const COLOR_FORECAST = '#ea580c';
const COLOR_BAND = 'rgba(234, 88, 12, 0.35)';

function formatDate(d: string): string {
  const [, m, day] = d.split('-');
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${parseInt(day, 10)} ${months[parseInt(m, 10) - 1] || m}`;
}

function formatRub(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`;
}

export default function AdminForecast() {
  const [horizon, setHorizonState] = useState<1 | 7 | 14 | 30>(() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_KEY_HORIZON) ?? '7', 10);
      return HORIZONS.includes(v as 1 | 7 | 14 | 30) ? (v as 1 | 7 | 14 | 30) : 7;
    } catch {
      return 7;
    }
  });
  const [data, setData] = useState<AdminForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ date: string; fact?: number; forecast?: number; lower?: number; upper?: number } | null>(null);

  const setHorizon = useCallback((h: 1 | 7 | 14 | 30) => {
    setHorizonState(h);
    try {
      localStorage.setItem(STORAGE_KEY_HORIZON, String(h));
    } catch {
      // ignore
    }
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    getAdminForecast(horizon)
      .then(setData)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Ошибка загрузки';
        setError(msg);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [horizon]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="admin-page">
        <div className="admin-skeleton-table" style={{ padding: '16px' }}>
          <div className="admin-skeleton-row">
            <div className="admin-skeleton-cell" style={{ width: '40%' }} />
            <div className="admin-skeleton-cell" style={{ width: '60%' }} />
          </div>
          <div className="admin-skeleton-row">
            <div className="admin-skeleton-cell" style={{ width: '100%' }} />
          </div>
          <div className="admin-skeleton-row">
            <div className="admin-skeleton-cell" style={{ width: '80%' }} />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    const isForbidden = /disabled|403|запрещён|отключён/i.test(error);
    return (
      <div className="admin-page">
        <div className="admin-card admin-empty">
          <p>{isForbidden ? 'Прогноз отключён в настройках' : error}</p>
          {isForbidden ? (
            <p className="admin-empty-hint">
              Обратитесь к администратору, если нужен доступ к прогнозу выручки.
            </p>
          ) : (
            <button type="button" onClick={fetchData} className="admin-btn admin-btn-primary" style={{ marginTop: '12px' }}>
              Повторить
            </button>
          )}
        </div>
      </div>
    );
  }

  const hist = data?.historical ?? [];
  const fore = data?.forecast ?? [];
  const hasValidResponse = Array.isArray(data?.historical) && Array.isArray(data?.forecast);
  if (!loading && data && !hasValidResponse) {
    return (
      <div className="admin-page">
        <div className="admin-card admin-empty">
          <p>Некорректный ответ сервера</p>
          <p className="admin-empty-hint">Попробуйте обновить данные.</p>
          <button type="button" onClick={fetchData} className="admin-btn admin-btn-primary" style={{ marginTop: '12px' }}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  const maxV = Math.max(
    1,
    ...hist.map((p) => p.value),
    ...fore.map((p) => p.upper)
  );
  const hasData = hist.length > 0 || fore.length > 0;
  const allZero = maxV <= 1 && hist.every((p) => p.value === 0) && fore.every((p) => p.value === 0);
  const showEmpty = !hasData;

  return (
    <div className="admin-page">
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        Прогноз выручки (baseline по дневной выручке) и доверительный интервал 2σ. Факт vs прогноз.
      </p>
      <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
        Прогноз строится по дневной выручке. Работает уже при малом количестве данных; с ростом истории точность повышается.
      </p>
      <div className="admin-filters" style={{ marginBottom: '16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Горизонт:</span>
        {HORIZONS.map((h) => (
          <button
            key={h}
            type="button"
            onClick={() => setHorizon(h)}
            className={`admin-btn ${horizon === h ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          >
            {h} дн.
          </button>
        ))}
        <button type="button" onClick={fetchData} className="admin-btn admin-btn-secondary" style={{ marginLeft: 'auto' }}>
          🔄 Обновить
        </button>
      </div>

      {data?.summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div className="admin-card" style={{ marginBottom: 0 }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>На завтра</div>
            <div style={{ fontSize: '18px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatRub(data.summary.tomorrow)}</div>
          </div>
          <div className="admin-card" style={{ marginBottom: 0 }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>На неделю</div>
            <div style={{ fontSize: '18px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatRub(data.summary.week)}</div>
          </div>
          <div className="admin-card" style={{ marginBottom: 0 }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>На месяц</div>
            <div style={{ fontSize: '18px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatRub(data.summary.month)}</div>
          </div>
        </div>
      )}

      {data?.tips && data.tips.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '16px', backgroundColor: 'var(--bg-secondary)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Подсказки</div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {data.tips.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {data?.mape14d != null && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-title">Точность (MAPE за 14 дней)</div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>{data.mape14d.toFixed(1)}%</div>
        </div>
      )}

      {showEmpty ? (
        <div className="admin-card admin-empty">
          <p>Нет данных за период</p>
          <p className="admin-empty-hint">
            Как только появятся оплаченные заказы, здесь покажется прогноз выручки. Прогноз работает уже с 1–2 дней данных и становится точнее с каждым новым днём.
          </p>
        </div>
      ) : (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          {allZero && (
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
              Пока нет оплаченных заказов за выбранный горизонт — показаны нулевые значения.
            </p>
          )}
          <div className="admin-card-title">Факт и прогноз (выручка по дням), ₽</div>
          <div style={{ display: 'flex', gap: '0', alignItems: 'stretch', minHeight: '200px' }}>
            {/* Y-axis labels (₽) */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                paddingRight: '8px',
                fontSize: '11px',
                color: 'var(--text-tertiary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <span>{formatRub(maxV)}</span>
              <span>{formatRub(maxV / 2)}</span>
              <span>0 ₽</span>
            </div>
            <div
              className="admin-chart-area"
              style={{
                flex: 1,
                display: 'flex',
                gap: '2px',
                alignItems: 'flex-end',
                height: '180px',
                overflowX: 'auto',
                paddingBottom: '24px',
              }}
            >
              {hist.map((p) => {
                const h = maxV > 0 ? (p.value / maxV) * 140 : 0;
                return (
                  <div
                    key={`h-${p.date}`}
                    style={{ flex: '0 0 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}
                    title={`${formatDate(p.date)}: факт ${formatRub(p.value)}`}
                    onMouseEnter={() => setTooltip({ date: p.date, fact: p.value })}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <div
                      style={{
                        width: '14px',
                        height: Math.max(4, h),
                        backgroundColor: COLOR_FACT,
                        borderRadius: '2px',
                      }}
                    />
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                      {formatDate(p.date)}
                    </div>
                  </div>
                );
              })}
              {fore.map((p: AdminForecastPoint, i: number) => {
                const h = maxV > 0 ? (p.value / maxV) * 140 : 0;
                const hLower = maxV > 0 ? (p.lower / maxV) * 140 : 0;
                const hUpper = maxV > 0 ? (p.upper / maxV) * 140 : 0;
                const bandHeight = Math.max(2, hUpper - hLower);
                return (
                  <div
                    key={`f-${p.date}-${i}`}
                    style={{ flex: '0 0 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}
                    title={`${formatDate(p.date)}: прогноз ${formatRub(p.value)} (2σ: ${formatRub(p.lower)} – ${formatRub(p.upper)})`}
                    onMouseEnter={() => setTooltip({ date: p.date, forecast: p.value, lower: p.lower, upper: p.upper })}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <div
                      style={{
                        width: '14px',
                        height: bandHeight,
                        minHeight: '2px',
                        backgroundColor: COLOR_BAND,
                        borderRadius: '2px',
                      }}
                    />
                    <div
                      style={{
                        width: '14px',
                        height: Math.max(4, h),
                        background: `repeating-linear-gradient(-45deg, ${COLOR_FORECAST}, ${COLOR_FORECAST} 2px, transparent 2px, transparent 4px)`,
                        borderRadius: '2px',
                      }}
                    />
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                      {formatDate(p.date)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {tooltip && (
            <div
              style={{
                marginTop: '8px',
                padding: '8px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-secondary)',
                fontSize: '12px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <strong>{formatDate(tooltip.date)}</strong>
              {tooltip.fact != null && <div>Факт: {formatRub(tooltip.fact)}</div>}
              {tooltip.forecast != null && (
                <div>
                  Прогноз: {formatRub(tooltip.forecast)}
                  {tooltip.lower != null && tooltip.upper != null && (
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: '6px' }}>
                      (2σ: {formatRub(tooltip.lower)} – {formatRub(tooltip.upper)})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '8px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLOR_FACT, borderRadius: '2px', marginRight: '6px', verticalAlign: 'middle' }} />
            Факт
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: `repeating-linear-gradient(-45deg, ${COLOR_FORECAST}, ${COLOR_FORECAST} 2px, transparent 2px, transparent 4px)`, borderRadius: '2px', marginLeft: '12px', marginRight: '6px', verticalAlign: 'middle' }} />
            Прогноз
            <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: COLOR_BAND, borderRadius: '2px', marginLeft: '12px', marginRight: '6px', verticalAlign: 'middle' }} />
            Интервал 2σ
          </div>
        </div>
      )}
    </div>
  );
}
